import {
  Alert,
  Button,
  Link,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Tab,
  Tabs,
} from '@heroui/react';
import { useMemo, useState } from 'react';
import _ from 'lodash';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { useGetPatientQuery } from '@/patient-app-common/api/patientApi';
import { Appointment } from '@/patient-app-common/types/Patient';

const mailto =
  'mailto:support@seamless.md?subject=SeamlessMD%20Support%20request%20-%20Appointment%20Help&body=Please%20include%20the%20following%20information%3A%0A%0APatient%20Name%3A%0AHospital%20Name%3A%0AEmail%3A%0APhone%20number%3A';

export function AppointmentList() {
  const { t } = useTranslation();
  const { data } = useGetPatientQuery();
  const [tab, setTab] = useState('active');

  const appointments = useMemo(() => {
    if (!data) return [];
    return data.appointments;
  }, [data]);

  const [activeAppointments, pastAppointments] = _.partition(
    appointments,
    (event) => dayjs().isSameOrBefore(event.startAt, 'd')
  );

  return (
    <div className="pt-8 w-full lg:w-3/4 mx-auto space-y-4">
      <h1 className="text-2xl">{t('common.link.appointments')}</h1>
      <Alert
        color="primary"
        hideIcon
        isVisible
        description="If any of the appointment dates or details are different from what your health care provider communicated, please contact us and we can help investigate."
        variant="faded"
      >
        <div className="mt-4 flex flex-col items-start">
          <Button
            as={Link}
            href={mailto}
            isExternal
            color="primary"
            size="sm"
            variant="flat"
          >
            {t('common.link.help')}
          </Button>
        </div>
      </Alert>
      <Tabs
        color="primary"
        size="md"
        selectedKey={tab}
        onSelectionChange={(key) => {
          setTab(key.toString());
        }}
        className="py-4"
        variant="bordered"
      >
        <Tab key="active" title="Active">
          <AppointmentTable appointments={activeAppointments} />
        </Tab>
        <Tab isDisabled={_.isEmpty(pastAppointments)} key="past" title="Past">
          <AppointmentTable appointments={pastAppointments} />
        </Tab>
      </Tabs>
    </div>
  );
}

function AppointmentTable({ appointments }: { appointments: Appointment[] }) {
  const { t } = useTranslation();

  return (
    <Table aria-label={t('common.link.appointments')} removeWrapper>
      <TableHeader>
        <TableColumn>Event</TableColumn>
        <TableColumn>Details</TableColumn>
        <TableColumn>Date</TableColumn>
        <TableColumn>Link (if applicable)</TableColumn>
      </TableHeader>
      <TableBody
        items={appointments}
        emptyContent={
          <p className="text-default-500">No upcoming appointments</p>
        }
      >
        {(event) => {
          const date = dayjs(event.startAt).format('llll');
          return (
            <TableRow key={event.title}>
              <TableCell>{event.title}</TableCell>
              <TableCell>{event.details}</TableCell>
              <TableCell>{date}</TableCell>
              <TableCell>
                {event.videoVisit ? (
                  <Button
                    as={Link}
                    color="primary"
                    href={event.url}
                    isExternal
                    size="sm"
                    variant="bordered"
                  >
                    Start video visit
                  </Button>
                ) : null}
              </TableCell>
            </TableRow>
          );
        }}
      </TableBody>
    </Table>
  );
}
